import React, { forwardRef, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  FormControl,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  IconButton,
  Stepper,
  Step,
  StepButton,
  StepLabel,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
} from "@material-ui/icons";
import { Formik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
  SELECTION_EXISTING,
  SELECTION_NEW,
  describeSelection,
  matchedBundleFieldError,
} from "src/modules/bundles/utils/bundle-modules";
import BasicInfoStep from "./steps/basic-info-step.component";
import AccessRightsStep from "./steps/access-rights-step.component";
import {
  STEPS,
  STEP_FIELDS,
  isApprove,
  lastStepIndex,
  monthsToExpiryDate,
} from "./approval-wizard.constants";

const useStyles = makeStyles((theme) => ({
  content: {
    maxHeight: "70vh",
    overflowY: "auto",
    backgroundColor: "#f3f3f3",
  },
  // The module cards live in ModuleSwitchGrid, which carries its own styles.
  applicantAccordion: {
    marginBottom: theme.spacing(2),
  },
}));

const Checkboxes = forwardRef((props, ref) => (
  <FormControl ref={ref} sx={{ padding: 2 }}>
    {props.options.map((option) => (
      <FormControlLabel
        key={option}
        label={option.charAt(0).toUpperCase() + option.slice(1)}
        name="rejectModules"
        value={option}
        checked={props.value.filter((v) => v === option).length > 0}
        onChange={props.onChange}
        disabled={props.disabled}
        control={<Checkbox value={option} />}
      />
    ))}
  </FormControl>
));

const REJECT_MODULE_OPTIONS_BASE = ["user", "company"];
const REJECT_MODULE_OPTIONS_WITH_PLAN = ["user", "company", "plan"];

// THE INVARIANT for this wizard (see bundle-modules): an approval may only assign the bundle
// the Bundle dropdown is showing. While the switches describe a DIFFERENT live bundle the two
// disagree, and the wizard refuses to guess which one the admin meant.
//
// The message lands on `bundle` for two reasons. It is the control that has to change; and
// `bundle` is already listed in STEP_FIELDS[1], so the block travels with the access-rights
// step — today that step is the last one for an approve, so the submit is what enforces it,
// but a third step added after it would inherit the same 'Next' gating for free.
//
// Blocked is not silent: Formik touches every field on a submit attempt, so
// AccessRightsStep's existing `helperText={touched.bundle && errors.bundle}` prints this
// under the dropdown, beside the banner that has been on screen since the switches drifted.
// The button still responds; it just says why it will not proceed.
//
// A `test` rather than a `when` branch because the message has to name the matched bundle,
// which needs the value; and it lives at module scope because Yup tests are `this`-bound,
// which a component body may not be.
const bundleIsTheOneBeingAssigned = (bundleOptions) =>
  function testBundleIsTheOneBeingAssigned(value) {
    const parent = this.parent || {};
    if (!isApprove(parent.approval)) {
      return true;
    }
    const selection = describeSelection(bundleOptions, value, parent.moduleIds);
    if (selection.status !== SELECTION_EXISTING) {
      return true;
    }
    return this.createError({
      message: matchedBundleFieldError(selection.matched),
    });
  };

const CompanyApprovalDialogComponent = ({
  industries,
  selectedCompany,
  onCompanyApprovalDialogSubmitClick,
  onCompanyApprovalDialogCloseClick,
  plans,
  isFetching,
  setSelectedPlan,
  selectedPlan,
  canEdit,
  bundleOptions,
  moduleListing,
  isLoaded,
  hasFailed,
  onRetryCatalogues,
}) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const rejectModuleOptions = selectedCompany
    && selectedCompany.owner.registrationType === 1
    ? REJECT_MODULE_OPTIONS_WITH_PLAN
    : REJECT_MODULE_OPTIONS_BASE;

  const rejectInputComponentProps = useMemo(
    () => ({
      inputComponent: Checkboxes,
      inputProps: { options: rejectModuleOptions },
    }),
    [rejectModuleOptions],
  );

  // The dialog is never unmounted (it renders null instead), so the wizard
  // position has to be reset explicitly — otherwise reopening it for a
  // different company lands on whatever step the previous review ended on.
  useEffect(() => {
    setActiveStep(0);
  }, [selectedCompany]);

  if (!selectedCompany) {
    return null;
  }

  return (
    <Dialog
      open={!!selectedCompany}
      onClose={onCompanyApprovalDialogCloseClick}
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      fullWidth
    >
      <Formik
        initialValues={{
          approval: "approve",
          remarks: "",
          rejectModules: [],
          plan: selectedPlan,
          isTestingAccount: false,
          bundle: -1,
          // Step 2 shows the WHOLE catalogue with the picked bundle's modules on, so the
          // switch state is wizard state like everything else. `newBundleName` only matters
          // when that state no longer matches any bundle.
          moduleIds: [],
          newBundleName: "",
          expireDate: "",
          salesEmail: "",
        }}
        validationSchema={Yup.object().shape({
          approval: Yup.string()
            .oneOf(["approve", "reject", "suspend"])
            .required("Approval is required"),
          remarks: Yup.string().when("approval", {
            is: (approval) => approval !== "approve",
            then: Yup.string().required("Remarks is a required field"),
          }),
          rejectModules: Yup.array().when("approval", {
            is: (approval) => approval === "reject",
            then: Yup.array()
              .of(Yup.string())
              .min(1, "Reject modules field must have at least 1 items"),
          }),
          bundle: Yup.number()
            .when("approval", {
              is: (approval) => approval === "approve",
              then: Yup.number()
                .min(1, "Bundle is a required field")
                .required("Bundle is a required field"),
            })
            // The consent rule, defined above. Chained AFTER the `when` on purpose: a
            // condition is resolved by concat'ing its branch onto this schema, so the test
            // survives resolution and runs for every verdict (it no-ops on reject/suspend).
            .test(
              "bundle-is-the-one-being-assigned",
              "Bundle is a required field",
              bundleIsTheOneBeingAssigned(bundleOptions)
            ),
          moduleIds: Yup.array(),
          // Only when the switches no longer match ANY live bundle: that selection has to
          // become a real bundle before it can be assigned, and a bundle needs a name. When
          // the selection matches another bundle nothing is created OR assigned — the rule
          // above blocks first — so no name is asked for there either.
          newBundleName: Yup.string().when(
            ["approval", "bundle", "moduleIds"],
            {
              is: (approval, bundle, moduleIds) => isApprove(approval)
                && describeSelection(bundleOptions, bundle, moduleIds).status
                  === SELECTION_NEW,
              then: Yup.string()
                .trim()
                .required("New bundle name is required"),
            }
          ),

          expireDate: Yup.string()
            .matches(/^\d+$/, "Enter number of months (e.g. 12, 16, 20)")
            .required("Expire Validity is required"),
          salesEmail: Yup.string()
            .email("Enter a valid email")
            .required("Sales Email is required"),
        })}
        enableReinitialize
        onSubmit={(values, actions) => {
          // Use object destructuring for expireDate
          const { expireDate: expireDateRaw, ...rest } = values;
          let expireDate = expireDateRaw;
          // If expireDate is a number (months), add to today
          if (/^\d+$/.test(expireDate)) {
            expireDate = monthsToExpiryDate(expireDate);
          }
          onCompanyApprovalDialogSubmitClick({ ...rest, expireDate }, actions);
        }}
      >
        {({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            setValues,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            validateForm,
          }) => (
          <form onSubmit={handleSubmit}>
            <DialogTitle id="scroll-dialog-title">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6">
                  {selectedCompany.company.name}
                  &nbsp;Approval
                </Typography>
                <IconButton
                  edge="end"
                  onClick={onCompanyApprovalDialogCloseClick}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            {/* px 16 + the Step's own 8px side padding puts the step icons on the title's 24px inset;
                the title's bottom padding spaces the row from above, pb matches it below. */}
            <Box px={2} pb={2}>
              <Stepper activeStep={activeStep} alternativeLabel={false}>
                {STEPS.map((s, i) => {
                  const skipped = !isApprove(values.approval) && i > 0;
                  return (
                    <Step key={s.key} disabled={skipped} completed={!skipped && i < activeStep}>
                      <StepButton
                        onClick={() => setActiveStep(i)}
                        // Editors advance step-by-step via the footer's Next (which validates
                        // the current step first), so a step ahead of the current one stays
                        // locked for them. A viewer has no footer and nothing to validate —
                        // let them read any non-skipped step, otherwise the read-only access-
                        // rights step would be unreachable.
                        disabled={skipped || (canEdit && i > activeStep)}
                      >
                        <StepLabel
                          optional={skipped ? (
                            <Typography variant="caption">
                              Not required to
                              {" "}
                              {values.approval}
                            </Typography>
                          ) : undefined}
                        >
                          {s.label}
                        </StepLabel>
                      </StepButton>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
            <DialogContent dividers className={classes.content}>
              <Box hidden={activeStep !== 0}>
                <BasicInfoStep
                  industries={industries}
                  selectedCompany={selectedCompany}
                  plans={plans}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  setSelectedPlan={setSelectedPlan}
                  setActiveStep={setActiveStep}
                  isSubmitting={isSubmitting}
                  canEdit={canEdit}
                  rejectInputComponentProps={rejectInputComponentProps}
                  classes={classes}
                />
              </Box>
              <Box hidden={activeStep !== 1}>
                <AccessRightsStep
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                  bundleOptions={bundleOptions}
                  moduleListing={moduleListing}
                  isLoaded={isLoaded}
                  hasFailed={hasFailed}
                  onRetry={onRetryCatalogues}
                  isFetching={isFetching}
                  isSubmitting={isSubmitting}
                  canEdit={canEdit}
                  classes={classes}
                />
              </Box>
            </DialogContent>
            {canEdit && (
              <DialogActions sx={{ padding: 2 }}>
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Box display="flex" gap={1}>
                    {activeStep > 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => setActiveStep(activeStep - 1)}
                        disabled={isSubmitting || isFetching}
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      onClick={onCompanyApprovalDialogCloseClick}
                      disabled={isSubmitting || isFetching}
                    >
                      Cancel
                    </Button>
                  </Box>
                  {activeStep < lastStepIndex(values.approval) ? (
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      disabled={isSubmitting || isFetching}
                      onClick={async () => {
                        const errs = await validateForm();
                        const blocking = STEP_FIELDS[activeStep].filter((f) => errs[f]);
                        if (blocking.length) {
                          blocking.forEach((f) => setFieldTouched(f, true, false));
                          return;
                        }
                        setActiveStep(activeStep + 1);
                      }}
                    >
                      Next: Access rights
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      color={isApprove(values.approval) ? "primary" : "error"}
                      disabled={!canEdit || isSubmitting || isFetching}
                    >
                      {isApprove(values.approval) ? "Approve Company"
                        : `${values.approval === "reject" ? "Reject" : "Suspend"} Company`}
                    </Button>
                  )}
                </Box>
              </DialogActions>
            )}
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

CompanyApprovalDialogComponent.propTypes = {
  industries: PropTypes.array.isRequired,
  selectedCompany: PropTypes.object,
  onCompanyApprovalDialogSubmitClick: PropTypes.func.isRequired,
  onCompanyApprovalDialogCloseClick: PropTypes.func.isRequired,
};

export default CompanyApprovalDialogComponent;
